import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal, Pager, Panel, useRemoteTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold } from "capitalrx-components-2.0/lib/typography";

import { getUrlForDownload } from "@/api/axios";
import { MemberDocument } from "@/api/types/Document";
import { useSelectedEnrollmentPlan } from "@/premium/hooks";
import { ThemedButton } from "@/premium/themedComponents";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

import "./DocumentsPanel.scss";

const translationPath = "PLAN_DETAILS.documentsPanel";

const DOCUMENT_RESULTS_PER_PAGE = 6;

const isIphoneUsingSafari = (() => {
  const isIphone = /iPhone/i.test(navigator.userAgent);
  const isSafari = !/CriOS/i.test(navigator.userAgent) && !/FxiOS/i.test(navigator.userAgent) && /Safari/i.test(navigator.userAgent);

  return isIphone && isSafari;
})();

export const DocumentsPanel = () => {
  const { t: translate } = useTranslation();
  const { enrollmentPlanCsid } = useSelectedEnrollmentPlan();
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<MemberDocument | null>(null);

  const { isMobile } = useResponsive({
    isMobile: { desktop: false, tablet: false, mobile: true },
  });

  const documentDataSource = useRemoteTableDataSource<{ document: MemberDocument }>(
    `${site.baseUrl}/document/list`,
    (row) => {
      return row.document.id;
    },
    {
      filter: { enrollment_plan_id: enrollmentPlanCsid?.id },
      authTokenRequired: false,
      pageSize: DOCUMENT_RESULTS_PER_PAGE,
      enabled: (filters) => !!filters.enrollment_plan_id,
    },
  );

  const openDoc = useCallback((url: string) => {
    if (isIphoneUsingSafari) {
      window.open(url, "_parent");
    } else {
      window.open(url, "_blank", "noreferrer");
    }
  }, []);

  const openDocument = useCallback(
    (doc: MemberDocument) => {
      logEvent(ANALYTIC_EVENTS.PLAN_DOCUMENT.new_tab);
      getUrlForDownload(doc.id, enrollmentPlanCsid.id, "view").then((response) => {
        openDoc(response.url);
      });
    },
    [openDoc, enrollmentPlanCsid],
  );

  const downloadDocument = useCallback(
    (doc: MemberDocument) => {
      logEvent(ANALYTIC_EVENTS.PLAN_DOCUMENT.download);
      getUrlForDownload(doc.id, enrollmentPlanCsid.id, "download").then((response) => {
        openDoc(response.url);
      });
    },
    [openDoc, enrollmentPlanCsid],
  );

  const handleViewMore = (document: MemberDocument) => {
    setModalVisible(true);
    setSelectedDocument(document);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedDocument(null);
  };

  return (
    <Panel>
      <Stack>
        <Heading>{translate(`${translationPath}.documents`)}</Heading>
        <Body>{translate(`${translationPath}.documentsInfo`)}</Body>
        <Modal className="documentModal" size="wide" title={selectedDocument?.name} visible={isModalVisible} onClose={handleCloseModal}>
          <Stack>
            <Body>{selectedDocument?.description}</Body>
            <Shelf>
              <Shelf.Spacer />
              <ThemedButton appearance="primary" onClick={handleCloseModal}>
                {translate(`${translationPath}.close`)}
              </ThemedButton>
            </Shelf>
          </Stack>
        </Modal>
        {documentDataSource.rows.map((row) => {
          return (
            <Stack spacing="small" className="bottomBorder" key={`${row.data.document.id}_document_row`}>
              <Stack spacing={isMobile ? "small" : "none"}>
                <Shelf>
                  <Semibold className="truncateOneLine">{row.data.document.name}</Semibold>
                  <Shelf.Spacer />
                  {!isMobile && (
                    <ThemedButton onClick={() => downloadDocument(row.data.document)} icon="download" appearance="text">
                      {translate(`${translationPath}.download`)}
                    </ThemedButton>
                  )}
                </Shelf>
                <Shelf>
                  <ThemedButton
                    onClick={() => openDocument(row.data.document)}
                    iconPosition="right"
                    $iconcolor="#0071B3"
                    icon="link-out"
                    appearance="text"
                    className="linkButton"
                  >
                    {translate(`${translationPath}.viewDocument`)}
                  </ThemedButton>
                  <Shelf.Spacer />
                </Shelf>
                {isMobile && (
                  <ThemedButton onClick={() => downloadDocument(row.data.document)} icon="download" appearance="text">
                    {translate(`${translationPath}.download`)}
                  </ThemedButton>
                )}
              </Stack>
              <Stack spacing="small">
                <Body className={isMobile ? "truncateThreeLines" : "truncateOneLine"}>{row.data.document.description}</Body>
                <Shelf>
                  <ThemedButton onClick={() => handleViewMore(row.data.document)} appearance="text">
                    {translate(`${translationPath}.viewMore`)}
                  </ThemedButton>
                  <Shelf.Spacer />
                </Shelf>
              </Stack>
            </Stack>
          );
        })}
        <Pager className="themedPager" dataSource={documentDataSource} />
      </Stack>
    </Panel>
  );
};
